import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useApi } from "@/lib/api";

const KEY = "jetset-trip-nav";
const HINT_KEY = "jetset-trip-swipe-hint-seen";
const TOUCH = "(pointer: coarse) and (max-width: 1366px)";
const IGNORE = "input, textarea, select, button, a, [role=dialog], [role=listbox], [data-testid$='-swipe'], [data-no-trip-swipe]";

export const saveTripNav = (trips, kind = "trips") => sessionStorage.setItem(`${KEY}-${kind}`, JSON.stringify(trips.map((t) => ({ id: t.id, ref: t.ref || t.trip_ref }))));

function useTripList(id, kind) {
  const api = useApi();
  const key = `${KEY}-${kind}`;
  const [list, setList] = useState(() => JSON.parse(sessionStorage.getItem(key) || "[]"));
  useEffect(() => {
    if (list.some((t) => t.id === id)) return;
    api.get(`/${kind}`).then((r) => { saveTripNav(r.data.trips || r.data, kind); setList(JSON.parse(sessionStorage.getItem(key))); }).catch(() => {});
  }, [api, id, list, kind, key]);
  const i = list.findIndex((t) => t.id === id);
  return i < 0 ? {} : { prev: list[i - 1], next: list[i + 1], index: i, total: list.length };
}

function useSwipe(go) {
  const start = useRef(null);
  useEffect(() => {
    if (!window.matchMedia(TOUCH).matches) return undefined;
    const down = (e) => {
      const t = e.touches[0];
      const edge = t.clientX < 30 || t.clientX > window.innerWidth - 30;
      start.current = e.touches.length > 1 || edge || e.target.closest(IGNORE) ? null : { x: t.clientX, y: t.clientY, at: Date.now() };
    };
    const up = (e) => {
      const s = start.current;
      start.current = null;
      if (!s) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - s.x;
      const dy = t.clientY - s.y;
      if (Math.abs(dx) > 80 && Math.abs(dy) < Math.abs(dx) * 0.5 && Date.now() - s.at < 700) go(dx < 0 ? 1 : -1);
    };
    window.addEventListener("touchstart", down, { passive: true });
    window.addEventListener("touchend", up, { passive: true });
    return () => { window.removeEventListener("touchstart", down); window.removeEventListener("touchend", up); };
  }, [go]);
}

function SwipeHint({ show, onDone }) {
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(onDone, 6000);
    return () => clearTimeout(t);
  }, [show, onDone]);
  if (!show) return null;
  return createPortal(
    <button type="button" onClick={onDone} data-no-trip-swipe
      className="on-navy swipe-hint fixed left-1/2 bottom-6 z-50 flex items-center gap-3 whitespace-nowrap rounded-full bg-[#0F1D3D]/95 border border-[#C6A55C]/50 px-5 py-3 text-[0.65rem] tracking-[0.2em] uppercase text-white shadow-[0_12px_30px_-10px_rgba(15,29,61,0.6)] backdrop-blur"
      data-testid="trip-swipe-hint">
      <ChevronLeft size={14} className="swipe-hint-l text-[#C6A55C]" />
      Swipe for the next trip
      <ChevronRight size={14} className="swipe-hint-r text-[#C6A55C]" />
    </button>,
    document.body,
  );
}

export default function TripSwipeNav({ id, kind = "trips" }) {
  const navigate = useNavigate();
  const { prev, next, index, total } = useTripList(id, kind);
  const goRef = useRef();
  goRef.current = (dir) => {
    const target = dir > 0 ? next : prev;
    if (!target) return;
    sessionStorage.setItem("jetset-swipe-dir", String(dir));
    navigate(`/${kind}/${target.id}`);
    window.scrollTo({ top: 0 });
  };
  const [go] = useState(() => (dir) => { dismissRef.current(); goRef.current(dir); });
  const [hint, setHint] = useState(false);
  const dismissRef = useRef(() => {});
  dismissRef.current = () => { if (hint) { localStorage.setItem(HINT_KEY, "1"); setHint(false); } };
  const [dismiss] = useState(() => () => dismissRef.current());
  useEffect(() => {
    if (total >= 2 && window.matchMedia(TOUCH).matches && !localStorage.getItem(HINT_KEY)) setHint(true);
  }, [total]);
  useSwipe(go);
  if (index === undefined || total < 2) return null;
  const btn = "p-1.5 text-zinc-300 hover:text-white disabled:opacity-30 transition-opacity";
  return (
    <div className="flex items-center gap-1 text-[0.62rem] tracking-[0.22em] uppercase text-zinc-300" data-testid="trip-swipe-nav">
      <button type="button" className={btn} onClick={() => go(-1)} disabled={!prev} aria-label="Previous trip" data-testid="trip-prev-btn"><ChevronLeft size={16} /></button>
      <span data-testid="trip-position">Trip {index + 1} of {total}</span>
      <button type="button" className={btn} onClick={() => go(1)} disabled={!next} aria-label="Next trip" data-testid="trip-next-btn"><ChevronRight size={16} /></button>
      <SwipeHint show={hint} onDone={dismiss} />
    </div>
  );
}
